import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import { useMemo } from "react";

function createMetafieldSchema(
  t: (key: Translation, options?: Record<string, unknown>) => string,
) {
  return z.object({
    key: z
      .string()
      .min(1, t("settings.customerPortal.form.metafields.key.required")),
    value: z
      .string()
      .min(1, t("settings.customerPortal.form.metafields.value.required")),
  });
}

function createCustomerPortalFormSchema(
  t: (key: Translation, options?: Record<string, unknown>) => string,
) {
  const metafieldSchema = createMetafieldSchema(t);

  return z.object({
    contactLink: z.string().min(1).url(),
    languages: z.array(z.string()).min(1),
    webOrderOnly: z.boolean(),
    nonReturnableItems: z.array(z.string().min(1)),
    tagsForNotAcceptedOrder: z.array(z.string().min(1)),
    canCancelReturnRequest: z.boolean(),
    restockOnReturn: z.boolean(),
    restockLocationId: z.string().optional().nullable(),
    autoTreateOnLogisticianValidate: z.boolean(),
    clientCanUpdatePersonalInformation: z.boolean(),
    refundInitialShippingRule: z.string(),
    refundInitialShippingAmountRule: z.number().optional().nullable(),
    oneResolutionPerReturn: z.boolean().optional().nullable(),
    singleArticleReturnOnly: z.boolean().optional().nullable(),
    groupItemsByFulfillment: z.boolean().optional().nullable(),
    allowOnlyUniqueReturnRequest: z.boolean().optional().nullable(),
    archiveOrderOnCancellation: z.boolean(),
    retractationEnabled: z.boolean(),
    retractationFallbackEnabled: z.boolean(),
    retractationFallbackDelayDays: z.number(),
    // BBS-342. Flattened rather than nested so the switch and its two lists
    // behave like the other conditional fields of this form; the card
    // reassembles them into `preferences.taxRefundPolicy`.
    taxRefundPolicyEnabled: z.boolean(),
    taxRefundPolicyExcludedCountryCodes: z.array(z.string()),
    taxRefundPolicyExcludedTaxTitles: z.array(z.string()),

    nonReturnableMetafieldItems: z.array(metafieldSchema),
  });
}

export type CustomerPortalFormValues = z.infer<
  ReturnType<typeof createCustomerPortalFormSchema>
>;

export const useCustomerPortalBasicsForm = (
  params: {
    defaultValues?: CustomerPortalFormValues;
  } = {},
) => {
  const t = useTranslation();

  const customerPortalFormSchema = useMemo(() => {
    return createCustomerPortalFormSchema(t);
  }, [t]);

  return useForm<CustomerPortalFormValues>({
    resolver: zodResolver(customerPortalFormSchema),
    defaultValues: params.defaultValues,
  });
};
